import { useEffect, useMemo, useRef, useState } from "react"
import { useEvent } from "lib/@hooks/useEvent"
import { Box, Portal, useTheme } from "@mui/material"
import { decomposeColor } from "@mui/material/styles"
import { diff3MergeRegions } from "node-diff3"
import { assembleBuffer, color, createHTMLComparisonArray } from "routes/schedule/compare/lib/diff3/utilities"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { expandMacroTokens } from "authoring/routes/macro/lib/macro-text-utils"
import { normalizeHighlightedBlockWrappers } from "./normalize-highlighted-block-wrappers"
import { markFormattedWords, stripFormattedWordMarkers } from "./mark-formatted-words"
import { prepareRichTextForRender } from "lib/prepare-rich-text-for-render"

export function Comparison({
    text,
    original,
    other,
    getContent = () => null,
    showRemoved,
    normalizeHtml: shouldNormalize,
}) {
    const theme = useTheme()
    // Authoring sets compareFormatting so a word that only changed formatting (made bold, say) shows as changed
    const { macroMap, compareFormatting = false } = useBoundContext()
    const noDiff = text === original && text === other
    text = prepareRichTextForRender(expandMacroTokens(text, macroMap))
    original = prepareRichTextForRender(expandMacroTokens(original, macroMap))
    other = prepareRichTextForRender(expandMacroTokens(other, macroMap))
    const normalizeHtmlFn =
        typeof shouldNormalize === "function" ? shouldNormalize : shouldNormalize ? normalizeHtml : null
    if (normalizeHtmlFn && !noDiff) {
        text = normalizeHtmlFn(text)
        original = normalizeHtmlFn(original)
        other = normalizeHtmlFn(other)
    }
    const [content, setContent] = useState(null)
    const [position, setPosition] = useState({ x: 0, y: 0 })
    const output = useRef()
    useEvent("MergeOver", (event, type) => {
        switch (type) {
            case "yours": {
                const userContent = getContent("yours", text, original, other)
                if (userContent) {
                    setPosition({ x: event.pageX, y: event.pageY })
                    setContent(userContent)
                }
                break
            }
            case "imported": {
                const userContent = getContent("remote", text, original, other)
                if (userContent) {
                    setPosition({ x: event.pageX, y: event.pageY })
                    setContent(userContent)
                }
                break
            }

            default: {
                const userContent = getContent("conflict", text, original, other)
                if (userContent) {
                    setPosition({ x: event.pageX, y: event.pageY })
                    setContent(userContent)
                }

                break
            }
        }
    })
    useEvent("MergeOut", () => setContent(null))
    const [, reasons] = useMemo(
        () => (noDiff ? [null, null] : compare(text, original, other, showRemoved, compareFormatting)),
        [text, other, original, showRemoved, noDiff, compareFormatting]
    )
    useEffect(() => {
        if (!output.current) {
            return
        }
        if (noDiff) {
            output.current.innerHTML = text ?? ""
        } else {
            output.current.innerHTML = reasons
            if (normalizeHtmlFn) {
                replaceSentinels(output.current)
            }
            normalizeHighlightedBlockWrappers(output.current)
        }
    }, [noDiff, reasons, text, normalizeHtmlFn])
    let tip = null
    if (content) {
        tip = (
            <Box
                sx={{
                    position: "absolute",
                    width: 350,
                    fontSize: "90%",
                    wordBreak: "break-word",
                    left: position.x - 100,
                    top: position.y + 20,
                    bgcolor: "background.default",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "action.disabled",
                }}
                width={200}
            >
                <Box p={2}>{content}</Box>
            </Box>
        )
    }
    return (
        <>
            <Portal>{tip}</Portal>
            <Box
                sx={computeHighlightStyles(theme)}
                style={{
                    overflow: "visible",
                    wordBreak: "break-word",
                }}
                ref={output}
            />
        </>
    )
}

function events(...params) {
    const command = `raise("MergeOver", event, ${params
        .map((p) => `\`${p.toString().replaceAll("'", "&apos;")}\``)
        .join(", ")})`
    return `onmouseover='${command}' onmouseout='raise("MergeOut")'`
}

function compare(a, o, b, showRemoved, compareFormatting) {
    const prepare = compareFormatting ? markFormattedWords : keepHtml
    const regions = diff3MergeRegions(
        createHTMLComparisonArray(prepare(a)),
        createHTMLComparisonArray(prepare(o)),
        createHTMLComparisonArray(prepare(b))
    )

    const combined = []
    const reasons = []

    for (const region of regions) {
        if (region.stable) {
            combined.push(region.bufferContent.join(" "))
            if (region.bufferContent.length === 1 && region.bufferContent[0] === "\n") region.buffer = "o"
            switch (region.buffer) {
                case "o":
                    reasons.push(assembleBuffer(region.bufferContent, "INSERTHERE", true))
                    break
                case "a":
                    reasons.push(
                        assembleBuffer(
                            region.bufferContent,
                            `<span class="schedule-text-difference" ${color(showRemoved ? window.currentTheme.palette.error.main : window.currentTheme.palette.primary.main, showRemoved)} ${events(
                                "yours",
                                region.bufferContent.join(" ")
                            )}>INSERTHERE</span>`
                        )
                    )

                    break
                default:
                    reasons.push(
                        assembleBuffer(
                            region.bufferContent,
                            `<span class="schedule-text-difference" ${color(window.currentTheme.palette.success.main)} ${events(
                                "imported",
                                region.bufferContent.join(" ")
                            )}>INSERTHERE</span>`
                        )
                    )

                    break
            }
        } else {
            combined.push(region.aContent.join(" "))
            reasons.push(
                assembleBuffer(
                    region.aContent,
                    `<span class="schedule-text-difference" ${color(
                        region.aContent.join(" ") !== region.bContent.join(" ")
                            ? window.currentTheme.palette.warning.main
                            : window.currentTheme.palette.primary.main
                    )} ${events(
                        region.aContent.join(" ") !== region.bContent.join(" ") ? "conflict" : "yours",
                        region.bContent.join(" ")
                    )} >INSERTHERE</span>`
                )
            )
        }
    }
    return [stripFormattedWordMarkers(combined.join(" ")), stripFormattedWordMarkers(reasons.join(" "))]
}

function keepHtml(html) {
    return html
}

export function ImportedChange({ change }) {
    return (
        <Box>
            <Box fontWeight="bold" mb={1}>
                This is a change to the original that did not conflict with any changes and so it has been included.
            </Box>
            <Box sx={{ maxHeight: 300, overflow: "auto" }} dangerouslySetInnerHTML={{ __html: change }} />{" "}
        </Box>
    )
}

export function YourChange({ change }) {
    return (
        <Box>
            <Box mb={1} fontWeight="bold">
                This is a change to the original
            </Box>
            <Box sx={{ maxHeight: 300, overflow: "auto" }} dangerouslySetInnerHTML={{ __html: change }} />
        </Box>
    )
}

export function ConflictChange({ change }) {
    return (
        <Box>
            <Box sx={{ fontWeight: "bold", mb: 1 }}>This change was selected, but it overwrote another change</Box>
            <Box mt={1}>Other change:</Box>
            <Box sx={{ fontStyle: "italic" }}>{change}</Box>
        </Box>
    )
}

/**
 * Normalize HTML to a consistent structure so the diff algorithm
 * can properly align matching content. Different editors produce different
 * HTML (plain text with <br> vs <p>-wrapped paragraphs) which causes
 * diff3 to treat identical text as changed.
 */
function normalizeHtml(value) {
    if (!value) return value
    // Extract text from each <p> block separately to preserve paragraph structure
    const div = document.createElement("div")
    div.innerHTML = value
    const paragraphs = []
    for (const node of div.childNodes) {
        if (node.nodeType === 3) {
            const t = node.textContent.trim()
            if (t) paragraphs.push(t)
        } else if (node.tagName === "P" || node.tagName === "DIV") {
            const t = node.textContent.trim()
            if (t) paragraphs.push(t)
            else paragraphs.push("") // empty <p><br></p> = blank line
        } else if (node.tagName === "OL" || node.tagName === "UL") {
            paragraphs.push("") // spacing before list (matches <ol>/<ul> margin)
            let index = parseInt(node.getAttribute("start") || "1", 10)
            for (const li of node.children) {
                if (li.tagName === "LI") {
                    const t = li.textContent.trim()
                    if (t) {
                        paragraphs.push(node.tagName === "OL" ? `${index}) ${t}` : t)
                        index++
                    }
                }
            }
            paragraphs.push("") // spacing after list (matches <ol>/<ul> margin)
        } else {
            const t = (node.textContent || "").trim()
            if (t) paragraphs.push(t)
        }
    }
    // Collapse consecutive blank lines
    const result = []
    for (const line of paragraphs) {
        if (line) {
            result.push(line.replaceAll("...", "\u2026"))
        } else if (result.length > 0 && result[result.length - 1] !== "") {
            result.push("") // single blank line marker
        }
    }
    // Strip trailing/leading blanks
    while (result.length > 0 && result[result.length - 1] === "") result.pop()
    while (result.length > 0 && result[0] === "") result.shift()
    if (!result.length) return value
    // Use a text sentinel between paragraphs — survives both stable and changed diff regions
    // because the tokenizer treats it as a regular word, not HTML structure.
    // Blank-line markers become an extra ___GAP___ so replaceSentinels produces
    // a double spacer div, preserving the visible blank line from the source.
    const content = result.map((l) => (l === "" ? "___GAP___" : l))
    return content.join(" ___GAP___ ")
}

function computeHighlightStyles(theme) {
    const [r, g, b] = decomposeColor(theme.palette.primary.main).values
    const [br, bg, bb] = decomposeColor(theme.palette.background.paper ?? "#fff").values
    const a = 0.07
    const highlight = `rgb(${Math.round(br + a * (r - br))}, ${Math.round(bg + a * (g - bg))}, ${Math.round(bb + a * (b - bb))})`
    // Restore paragraph spacing in both diff and plain rich-text rendering so the
    // comparison view matches the preview. The global CssBaseline `* { margin: 0 }`
    // reset wins over browser-default <p> margins, so we re-apply with !important to
    // guard against any other descendant selectors that might also be zeroing margins.
    return {
        "& p": { marginTop: "0 !important", marginBottom: "1em !important" },
        "& span[onmouseover]": {
            backgroundColor: highlight,
            borderRadius: "4px",
            padding: "2px 3px",
            margin: "0 -3px",
            boxDecorationBreak: "clone",
            WebkitBoxDecorationBreak: "clone",
        },
    }
}

function replaceSentinels(container) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
    const matches = []
    let node
    while ((node = walker.nextNode())) {
        if (node.textContent.includes("___GAP___")) {
            matches.push(node)
        }
    }
    for (const textNode of matches) {
        const spacer = document.createElement("div")
        spacer.style.height = "16px"
        const parent = textNode.parentElement
        if (parent?.classList?.contains("schedule-text-difference")) {
            // Split the text at each ___GAP___ and keep non-empty parts
            // wrapped in clones of the diff span so highlighting is preserved
            const parts = textNode.textContent.split("___GAP___")
            const grandparent = parent.parentNode
            parts.forEach((part, i) => {
                if (i > 0) grandparent.insertBefore(spacer.cloneNode(), parent)
                const trimmed = part.trim()
                if (trimmed) {
                    const clone = parent.cloneNode(false)
                    clone.textContent = part
                    grandparent.insertBefore(clone, parent)
                }
            })
            parent.remove()
        } else {
            // Replace the sentinel text with the spacer
            const parts = textNode.textContent.split("___GAP___")
            const frag = document.createDocumentFragment()
            parts.forEach((part, i) => {
                if (i > 0) frag.appendChild(spacer.cloneNode())
                if (part) frag.appendChild(document.createTextNode(part))
            })
            textNode.parentNode.replaceChild(frag, textNode)
        }
    }
}
